import React, { useState, useEffect } from 'react';
import { MessageSquare, Lightbulb, TrendingUp } from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import { api } from '../../api/client';

const AssistantTab = () => {
  const { activeSession, currentUser } = useAppContext();
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Load chat history when component mounts
  useEffect(() => {
    if (activeSession) {
      loadChatHistory();
    }
  }, [activeSession]);

  const loadChatHistory = async () => {
    try {
      const history = await api.getChatHistory(activeSession.id);
      
      // Convert to chat message format
      const messages = history.flatMap(h => [
        {
          id: `${h.id}-user`,
          sender: 'You',
          text: h.message,
          timestamp: h.created_at,
          isAI: false
        },
        {
          id: `${h.id}-ai`,
          sender: 'AI Assistant',
          text: h.response,
          timestamp: h.created_at,
          isAI: true
        }
      ]);
      
      setChatMessages(messages);
    } catch (error) {
      console.error('Failed to load chat history:', error);
    }
  };

  const sendChatMessage = async (message) => {
    const userMessage = {
      id: `temp-${Date.now()}`,
      sender: 'You',
      text: message,
      timestamp: new Date().toISOString(),
      isAI: false
    };
    
    setChatMessages([...chatMessages, userMessage]);
    setChatInput('');
    setIsLoading(true);
    
    try {
      const response = await api.chatWithAI(message, activeSession?.id);
      
      const aiResponse = {
        id: `ai-${response.chat_id}`,
        sender: 'AI Assistant',
        text: response.response,
        timestamp: new Date().toISOString(),
        isAI: true
      };
      setChatMessages(prev => [...prev, aiResponse]);
    } catch (error) {
      console.error('AI chat error:', error);
      const errorResponse = {
        id: `error-${Date.now()}`,
        sender: 'AI Assistant',
        text: 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date().toISOString(),
        isAI: true
      };
      setChatMessages(prev => [...prev, errorResponse]);
    } finally {
      setIsLoading(false);
    }
  };

  const getSuggestions = async () => {
    if (!activeSession) return;
    
    setIsLoading(true);
    try {
      const suggestions = await api.suggestUncertainties(activeSession.id);
      
      const suggestionText = suggestions.length > 0
        ? `I've identified ${suggestions.length} potential uncertainties:\n\n` +
          suggestions.map((s, i) => 
            `${i + 1}. **${s.title}** (${s.category})\n   ${s.description}`
          ).join('\n\n')
        : 'I couldn\'t generate suggestions at this time. Try providing more context about your session.';
      
      const aiResponse = {
        id: `suggestion-${Date.now()}`,
        sender: 'AI Assistant',
        text: suggestionText,
        timestamp: new Date().toISOString(),
        isAI: true
      };
      setChatMessages(prev => [...prev, aiResponse]);
    } catch (error) {
      console.error('Suggestion error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const analyzeVotes = async () => {
    if (!activeSession) return;
    
    setIsLoading(true);
    try {
      const result = await api.analyzeVotes(activeSession.id);
      
      const aiResponse = {
        id: `analysis-${Date.now()}`,
        sender: 'AI Assistant',
        text: result.analysis,
        timestamp: new Date().toISOString(),
        isAI: true
      };
      setChatMessages(prev => [...prev, aiResponse]);
    } catch (error) {
      console.error('Analysis error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && chatInput.trim() && !isLoading) {
      sendChatMessage(chatInput);
    }
  };

  return (
    <div className="flex flex-col h-[600px]">
      <div className="flex-1 overflow-y-auto bg-gray-50 rounded-lg p-4 mb-4 space-y-4">
        {chatMessages.length === 0 && (
          <div className="text-center text-gray-500 py-12">
            <MessageSquare className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <p className="font-medium mb-2">AI-Powered Strategic Foresight Assistant</p>
            <p className="text-sm">Ask questions, get insights, or use quick actions below to get started.</p>
          </div>
        )}
        
        {chatMessages.map(msg => (
          <div
            key={msg.id}
            className={`flex ${msg.isAI ? 'justify-start' : 'justify-end'}`}
          >
            <div
              className={`max-w-[70%] rounded-lg p-4 ${
                msg.isAI
                  ? 'bg-white border border-gray-200'
                  : 'bg-[#01006A] text-white'
              }`}
            >
              <div className="flex items-center space-x-2 mb-1">
                <span className="text-xs font-semibold">
                  {msg.sender}
                </span>
                <span className="text-xs opacity-70">
                  {new Date(msg.timestamp).toLocaleTimeString()}
                </span>
              </div>
              <div className="text-sm whitespace-pre-wrap">{msg.text}</div>
            </div>
          </div>
        ))}
        
        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-[#01006A] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-2 h-2 bg-[#01006A] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-2 h-2 bg-[#01006A] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="flex space-x-2 mb-4">
        <input
          type="text"
          value={chatInput}
          onChange={(e) => setChatInput(e.target.value)}
          onKeyPress={handleKeyPress}
          disabled={isLoading}
          placeholder="Ask the AI assistant..."
          className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#01006A] focus:border-transparent disabled:bg-gray-100"
        />
        <button
          onClick={() => {
            if (chatInput.trim() && !isLoading) {
              sendChatMessage(chatInput);
            }
          }}
          disabled={isLoading || !chatInput.trim()}
          className="bg-[#01006A] text-white px-6 py-3 rounded-lg hover:bg-[#010059] font-semibold disabled:bg-gray-400"
        >
          Send
        </button>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <button
          onClick={() => sendChatMessage("Summarize the current session and key findings")}
          disabled={isLoading}
          className="flex items-center justify-center space-x-2 text-sm bg-gray-100 text-gray-700 px-3 py-2 rounded hover:bg-gray-200 disabled:bg-gray-50"
        >
          <MessageSquare className="w-4 h-4" />
          <span>Summarize Session</span>
        </button>
        <button
          onClick={getSuggestions}
          disabled={isLoading}
          className="flex items-center justify-center space-x-2 text-sm bg-gray-100 text-gray-700 px-3 py-2 rounded hover:bg-gray-200 disabled:bg-gray-50"
        >
          <Lightbulb className="w-4 h-4" />
          <span>Suggest Uncertainties</span>
        </button>
        <button
          onClick={analyzeVotes}
          disabled={isLoading}
          className="flex items-center justify-center space-x-2 text-sm bg-gray-100 text-gray-700 px-3 py-2 rounded hover:bg-gray-200 disabled:bg-gray-50"
        >
          <TrendingUp className="w-4 h-4" />
          <span>Analyze Votes</span>
        </button>
        <button
          onClick={() => sendChatMessage("Help me develop scenarios based on critical uncertainties")}
          disabled={isLoading}
          className="flex items-center justify-center space-x-2 text-sm bg-gray-100 text-gray-700 px-3 py-2 rounded hover:bg-gray-200 disabled:bg-gray-50"
        >
          <span>🎯</span>
          <span>Develop Scenarios</span>
        </button>
      </div>
    </div>
  );
};

export default AssistantTab;